/**
 * AnimatedNotificationBar - شريط إشعارات برتقالي ثابت مع صوت
 * يعرض البيانات لمدة 5 ثوانٍ ثم يختفي
 */
import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { safeLocalStorage } from '../utils/safeStorage';

// Notification sound (base64 encoded short beep)
const NOTIFICATION_SOUND = 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleVg7gKq7oG0zFVBzkrbSpnRLLWqdz+eukmE/RXeJl4x3WkJKcYqVj35dREtjdoKBdGRaVV5sdHpybWdjY2duc3h2cnByb3Z6fHt4d3l4e3x9fXx6e3p7fH18fHx7e3t8fHx8fHx8fHx8fHx8fHx8fHx8fHx8fHx8fHx8fHx8fHx8fHx8fHx8fHx8fHx8';

// Store for notifications
let notificationListeners = [];
let notificationQueue = [];

// Get sound preference from localStorage
const getSoundEnabled = () => {
  try {
    const saved = safeLocalStorage.getItem('notificationSound');
    return saved !== null ? JSON.parse(saved) : true;
  } catch {
    return true;
  }
};

// Play notification sound
const playSound = () => {
  if (getSoundEnabled()) {
    try {
      const audio = new Audio(NOTIFICATION_SOUND);
      audio.volume = 0.5;
      audio.play().catch(() => {});
    } catch (e) {}
  }
};

// Public API to show notifications
export const showNotification = (message, options = {}) => {
  const notification = {
    id: Date.now(),
    message,
    type: options.type || 'info',
    duration: options.duration || 5000, // 5 ثوانٍ
    memberCount: options.memberCount,
    newMembersCount: options.newMembersCount,
    playSound: options.playSound !== false, // تشغيل الصوت افتراضياً
    ...options
  };
  
  // Play sound when notification appears
  if (notification.playSound) {
    playSound();
  }
  
  notificationQueue.push(notification);
  notificationListeners.forEach(listener => listener([...notificationQueue]));
  
  // Auto remove after duration
  setTimeout(() => {
    notificationQueue = notificationQueue.filter(n => n.id !== notification.id);
    notificationListeners.forEach(listener => listener([...notificationQueue]));
  }, notification.duration);
};

// Custom hook to subscribe to notifications
const useNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  
  useEffect(() => {
    notificationListeners.push(setNotifications);
    return () => {
      notificationListeners = notificationListeners.filter(l => l !== setNotifications);
    };
  }, []);
  
  return notifications;
};

// Single notification bar component - عرض ثابت لمدة 5 ثوانٍ
const NotificationBar = ({ notification, isRTL, onComplete }) => {
  const [visible, setVisible] = useState(true);
  const [fadeOut, setFadeOut] = useState(false);
  
  useEffect(() => {
    // Start fade out 500ms before end
    const fadeTimer = setTimeout(() => {
      setFadeOut(true);
    }, notification.duration - 500);
    
    // Complete removal
    const removeTimer = setTimeout(() => {
      setVisible(false);
      if (onComplete) onComplete();
    }, notification.duration);
    
    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(removeTimer);
    };
  }, [notification.duration, onComplete]);
  
  if (!visible) return null;

  // تحديد النص بناءً على نوع الإشعار
  let displayText = notification.message;
  
  // إذا كان هناك بيانات أعضاء، اعرضها
  if (notification.memberCount !== undefined || notification.newMembersCount !== undefined) {
    const memberCount = notification.memberCount || 0;
    const newCount = notification.newMembersCount || 0;
    
    if (isRTL) {
      displayText = `🔔 تنبيه جديد!  |  إجمالي الأعضاء المجمعين: ${memberCount}  |  +${newCount} عضو جديد`;
    } else {
      displayText = `🔔 New Alert!  |  Total Collected Members: ${memberCount}  |  +${newCount} new members`;
    }
  }
  
  return (
    <div
      className="notification-bar-container"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: '56px',
        backgroundColor: '#FF8C00',
        zIndex: 99999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 4px 15px rgba(255, 140, 0, 0.5)',
        opacity: fadeOut ? 0 : 1,
        transition: 'opacity 0.5s ease-out',
        direction: isRTL ? 'rtl' : 'ltr',
      }}
    >
      <div
        className="notification-bar-content"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px',
          fontWeight: 'bold',
          fontSize: '18px',
          color: '#000000',
          padding: '0 24px',
          textAlign: 'center',
        }}
      >
        <span>{displayText}</span>
      </div>
    </div>
  );
};

// Main notification provider component
export const AnimatedNotificationBar = ({ isRTL = false }) => {
  const notifications = useNotifications();
  const [currentNotification, setCurrentNotification] = useState(null);
  
  useEffect(() => {
    if (notifications.length > 0 && !currentNotification) {
      setCurrentNotification(notifications[0]);
    }
  }, [notifications, currentNotification]);
  
  const handleComplete = useCallback(() => {
    setCurrentNotification(null);
  }, []);
  
  if (!currentNotification) return null;
  
  return createPortal(
    <NotificationBar 
      notification={currentNotification} 
      isRTL={isRTL}
      onComplete={handleComplete}
    />,
    document.body
  );
};

export default AnimatedNotificationBar;
